"use client";
import { useState, useEffect, useRef } from 'react';
import * as LucideIcons from 'lucide-react';
import * as FaIcons from 'react-icons/fa6';
const Icons = { ...LucideIcons, ...FaIcons };
import Link from 'next/link';
import styles from "./feature-image-top.module.css";
import { componentDefaults } from "./data";
import { createUpdateHandler } from "./component-helpers";


const openDialog = (id) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('lunar:open-dialog', { detail: { id } }));
  }
};
const PlayIcon = ({ style }) => <svg style={style} fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>;

const BuilderSection = ({ tagName = 'div', className, innerContainer, fullWidth, style, children, id, sectionId, isVisible = true, removePaddingLeft, removePaddingRight, onUpdate, ...rest }) => {
  if (!isVisible) return null;
  const Tag = tagName;
  const normalizedSectionId = (sectionId && typeof sectionId === 'string') ? sectionId.replace(/-+$/, '') : '';
  let finalId = id || normalizedSectionId;
  finalId = finalId ? finalId.replace(/-+/g, '-') : undefined;
  const containerClasses = ["container-grid"];
  if (removePaddingLeft === true || removePaddingLeft === "true") containerClasses.push("pl-0");
  if (removePaddingRight === true || removePaddingRight === "true") containerClasses.push("pr-0");
  if (fullWidth === true || fullWidth === "true") containerClasses.push("container-full");
  const containerClass = containerClasses.join(" ");
  if (innerContainer) {
    return (
      <Tag id={finalId} className={className} style={style}>
        <div className={containerClass}>{children}</div>
      </Tag>
    );
  }
  return <Tag id={finalId} className={containerClass + " " + (className || '')} style={style}>{children}</Tag>;
};

const BuilderText = ({ tagName = 'p', content, className, style, children, id, sectionId, suffix, isVisible = true, tooltipIfTruncated, onUpdate, ...rest }) => {
  if (!isVisible) return null;
  const Tag = tagName;
  const normalizedSectionId = (sectionId && typeof sectionId === 'string') ? sectionId.replace(/-+$/, '') : '';
  const effectiveSuffix = suffix || (className ? className.split(' ')[0] : tagName);
  let finalId = id || (normalizedSectionId ? normalizedSectionId + '-' + effectiveSuffix : undefined);
  finalId = finalId ? finalId.replace(/-+/g, '-') : undefined;
  const finalClassName = ("builder-text " + (className || '') + " " + (!content && !children ? 'empty-builder-text' : '')).trim();
  const title = tooltipIfTruncated ? content : undefined;
  const isSimpleString = content && typeof content === 'string' && !/<[a-z]|&[a-z0-9#]+;/i.test(content);
  return (
    <Tag
      id={finalId}
      className={finalClassName}
      style={style}
      dangerouslySetInnerHTML={content && !isSimpleString ? { __html: content } : undefined}
      title={title}
    >
      {isSimpleString ? content : (!content ? children : null)}
    </Tag>
  );
};

const BuilderButton = ({ label, href, openInNewTab, className, style, children, linkType, targetDialogId, id, sectionId, suffix, iconLeft, iconRight, hideLabel, isVisible = true, onUpdate, isSubmit, disabled, isLoading, onClick, disableLinkSettings, ...rest }) => {
  if (!isVisible) return null;
  const normalizedSectionId = (sectionId && typeof sectionId === 'string') ? sectionId.replace(/-+$/, '') : '';
  let finalId = id || (normalizedSectionId && suffix ? normalizedSectionId + '-' + suffix : undefined);
  finalId = finalId ? finalId.replace(/-+/g, '-') : undefined;
  const renderIcon = (icon) => {
    if (typeof icon === 'string' && typeof Icons !== 'undefined' && Icons[icon]) {
      const IconComponent = Icons[icon];
      return <IconComponent style={{ width: '1.25rem', height: '1.25rem' }} />;
    }
    return typeof icon === 'string' ? null : icon;
  };
  const isSimpleLabel = label && typeof label === 'string' && !/<[a-z]|&[a-z0-9#]+;/i.test(label);
  const content = (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', gap: 'inherit', opacity: isLoading ? 0.5 : 1 }}>
         {isLoading && <div className="spinner-sm" style={{ marginRight: '8px' }}></div>}
         {!isLoading && renderIcon(iconLeft) && <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{renderIcon(iconLeft)}</span>}
          {!hideLabel && (
             <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {isSimpleLabel ? label : (label && typeof label === 'string' ? <span dangerouslySetInnerHTML={{ __html: label }} /> : (label || children))}
             </div>
          )}
         {!isLoading && renderIcon(iconRight) && <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{renderIcon(iconRight)}</span>}
      </div>
  );
  const finalClassName = className ? (className.split(' ').includes('btn') ? className : ('btn ' + className)) : 'btn';
  const isLink = !!href || (linkType === 'dialog' && !!targetDialogId);
  if (isSubmit) {
    return <button id={finalId} type="submit" disabled={disabled || isLoading} className={finalClassName} style={{ ...style, textDecoration: 'none' }} onClick={onClick}>{content}</button>;
  }
  if (!isLink) {
    return <button id={finalId} type="button" disabled={disabled || isLoading} className={finalClassName} style={{ ...style, textDecoration: 'none' }} onClick={onClick}>{content}</button>;
  }
  const isNewTab = openInNewTab === true || openInNewTab === 'true';
  const targetAttr = isNewTab ? '_blank' : undefined;
  const relAttr = isNewTab ? 'noopener noreferrer' : 'noopener';

  if (linkType === 'dialog' && targetDialogId) {
    return (
      <a id={finalId} href="#" className={finalClassName} style={{ ...style, cursor: 'pointer', textDecoration: 'none' }} rel={relAttr} onClick={(e) => { e.preventDefault(); if (onClick) onClick(e); openDialog(targetDialogId); }}>{content}</a>
    );
  }
  return <a id={finalId} href={href || '#'} target={targetAttr} className={finalClassName} style={{ ...style, textDecoration: 'none' }} rel={relAttr} onClick={onClick}>{content}</a>;
};

const BuilderImage = ({ src, mobileSrc, alt, className, style, mobileRatio, href, openInNewTab, linkType, targetDialogId, id, sectionId, suffix, isPortrait, isVisible = true, priority, aspectRatio, alwaysShowSrc, showStroke, showShadow, autoplay, enableAudio, onUpdate, watermarkSrc, ...rest }) => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const isAutoplay = autoplay === true || autoplay === "true" || autoplay === undefined;
  const [isAudioEnabled, setIsAudioEnabled] = useState(enableAudio === true || enableAudio === "true");
  const [isPlaying, setIsPlaying] = useState(isAutoplay);
  const wrapperRef = useRef(null);
  const videoRef = useRef(null);

  const isVideoFile = (url) => url && typeof url === 'string' && url.match(/\.(mp4|webm|ogg|mov)$/i);
  const isYoutube = (url) => url && typeof url === 'string' && url.match(/^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.*$/);
  const isVimeo = (url) => url && typeof url === 'string' && url.match(/^(https?:\/\/)?(www\.)?(vimeo\.com)\/.*$/);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const needsImmediateLoad = !!(priority || alwaysShowSrc || !src || src === "" || (!typeof window === 'undefined' && !window.IntersectionObserver));
    if (needsImmediateLoad) { setShouldLoad(true); return; }
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) { setShouldLoad(true); observer.disconnect(); }
    }, { threshold: 0.01, rootMargin: '200px' });
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, [src, priority, alwaysShowSrc, href, linkType]);

  useEffect(() => {
    if (isAutoplay && shouldLoad && videoRef.current) {
      videoRef.current.play().catch(() => {
        setIsPlaying(false);
      });
    }
  }, [isAutoplay, shouldLoad]);

  if (!isVisible) return null;
  const normalizedSectionId = (sectionId && typeof sectionId === 'string') ? sectionId.replace(/-+$/, '') : '';
  let finalId = id || (normalizedSectionId && suffix ? normalizedSectionId + '-' + suffix : undefined);
  finalId = finalId ? finalId.replace(/-+/g, '-') : undefined;
  const effectiveAlt = alt || '';
  const isStrokeEnabled = showStroke === true || showStroke === "true";
  const isShadowEnabled = showShadow === true || showShadow === "true";
  let baseClassName = (className || '') + ' builder-image-container' + (isStrokeEnabled ? ' has-stroke' : '') + (isShadowEnabled ? ' has-shadow' : '');
  if (mobileRatio) baseClassName += " mobile-aspect-" + mobileRatio;

  const mediaStyle = { width: "100%", height: "100%", objectFit: ((className || '').includes('navigation') || (className || '').includes('logo') || (className || '').includes('object-contain')) ? 'contain' : 'cover', display: "block", ...style };
  const mediaClass = 'builder-image-media';

  const getYoutubeEmbedUrl = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    const id = match && match[2].length === 11 ? match[2] : null;
    return id ? "https://www.youtube.com/embed/" + id + "?autoplay=" + (isAutoplay ? 1 : 0) + "&mute=" + (isAudioEnabled ? 0 : 1) + "&loop=1&playlist=" + id + "&controls=0" : url;
  };

  const getVimeoEmbedUrl = (url) => {
    const match = url.match(/vimeo\.com\/(\d+)/);
    const id = match ? match[1] : null;
    return id ? "https://player.vimeo.com/video/" + id + "?autoplay=" + (isAutoplay ? 1 : 0) + "&loop=1&muted=" + (isAudioEnabled ? 0 : 1) + "&background=" + (isAutoplay ? 1 : 0) : url;
  };

  let mediaContent;
  if (isYoutube(src)) {
    mediaContent = shouldLoad ? <iframe src={getYoutubeEmbedUrl(src)} className={mediaClass} style={{ ...mediaStyle, border: 'none' }} allow="autoplay; fullscreen" allowFullScreen /> : <div className={mediaClass} style={mediaStyle} />;
  } else if (isVimeo(src)) {
    mediaContent = shouldLoad ? <iframe src={getVimeoEmbedUrl(src)} className={mediaClass} style={{ ...mediaStyle, border: 'none' }} allow="autoplay; fullscreen" allowFullScreen /> : <div className={mediaClass} style={mediaStyle} />;
  } else if (isVideoFile(src)) {
    const videoSrc = (src && !src.startsWith('http') ? encodeURI(src) : src) + (!isAutoplay ? "#t=0.001" : "");
    mediaContent = (
      <>
        <video 
          key={src}
          ref={videoRef} 
          className={mediaClass} 
          style={mediaStyle} 
          autoPlay={isAutoplay && shouldLoad} 
          loop 
          muted={!isAudioEnabled} 
          playsInline 
          preload={!isAutoplay ? "auto" : (shouldLoad ? "metadata" : "none")} 
          onPlay={() => setIsPlaying(true)} 
          onPause={() => setIsPlaying(false)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (videoRef.current && !videoRef.current.paused) {
              videoRef.current.pause();
            }
          }}
        >
          {shouldLoad && (
            <>
              {mobileSrc && <source src={mobileSrc && !mobileSrc.startsWith('http') ? encodeURI(mobileSrc) : mobileSrc} media="(max-width: 767px)" />}
              {src && <source src={videoSrc} />}
            </>
          )}
          Your browser does not support the video tag.
        </video>
        {!isPlaying && (
          <button type="button" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: 'rgba(0,0,0,0.6)', borderRadius: '50%', width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10, border: 'none' }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (videoRef.current) videoRef.current.play(); }}>
            <PlayIcon style={{ color: 'white', width: '32px', height: '32px' }} />
          </button>
        )}
      </>
    );
  } else {
    const imgContent = <img src={src || "https://space.lunaaar.site/assets-lunar/placeholder.svg"} alt={effectiveAlt} className={mediaClass} style={mediaStyle} loading={priority ? "eager" : "lazy"} crossOrigin={rest.crossOrigin} />;
    mediaContent = shouldLoad ? (
      mobileSrc ? (
        <picture style={{ display: 'contents' }}>
          <source media="(max-width: 767px)" srcSet={mobileSrc} />
          {imgContent}
        </picture>
      ) : imgContent
    ) : <div className={mediaClass} style={mediaStyle} />;
  }

  const wrapperStyle = { display: 'block', width: '100%', height: '100%', position: 'relative', overflow: 'hidden', ...style };

  const muteButton = (isYoutube(src) || isVimeo(src) || isVideoFile(src)) ? (
    <button
      type="button"
      className="media-mute-button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsAudioEnabled(!isAudioEnabled);
      }}
    >
      {isAudioEnabled ? (
        <svg className="media-mute-icon" fill="currentColor" viewBox="0 0 24 24"><path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 001.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06zM18.584 5.106a.75.75 0 011.06 0c3.808 3.807 3.808 9.98 0 13.788a.75.75 0 11-1.06-1.06 8.25 8.25 0 000-11.668.75.75 0 010-1.06z" /><path d="M15.932 7.757a.75.75 0 011.061 0 6 6 0 010 8.486.75.75 0 01-1.06-1.061 4.5 4.5 0 000-6.364.75.75 0 010-1.06z" /></svg>
      ) : (
        <svg className="media-mute-icon" fill="currentColor" viewBox="0 0 24 24"><path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 001.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06zM17.78 9.22a.75.75 0 10-1.06 1.06L18.44 12l-1.72 1.72a.75.75 0 001.06 1.06l1.72-1.72 1.72 1.72a.75.75 0 101.06-1.06L20.56 12l1.72-1.72a.75.75 0 00-1.06-1.06l-1.72 1.72-1.72-1.72z" /></svg>
      )}
    </button>
  ) : null;

  const watermarkContent = watermarkSrc ? (
    <img 
      src={watermarkSrc} 
      alt="Watermark" 
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        opacity: 1.0,
        pointerEvents: 'none',
        zIndex: 2
      }} 
    />
  ) : null;

  const isNewTab = openInNewTab === true || openInNewTab === 'true';
  const targetAttr = isNewTab ? '_blank' : undefined;
  const relAttr = isNewTab ? 'noopener noreferrer' : 'noopener';
  const isMediaVideo = isVideoFile(src) || isYoutube(src) || isVimeo(src);

  if (!isMediaVideo && (href || (linkType === 'dialog' && targetDialogId))) {
    return <a ref={wrapperRef} id={finalId} href={href || '#'} target={targetAttr} rel={relAttr} className={baseClassName} style={wrapperStyle} onClick={(e) => { if (linkType === 'dialog') { e.preventDefault(); openDialog(targetDialogId); } }}>{mediaContent}{muteButton}{watermarkContent}</a>;
  }
  return <div ref={wrapperRef} className={baseClassName} style={wrapperStyle}>{mediaContent}{muteButton}{watermarkContent}</div>;
};
export default function FeatureImageTop({
category = componentDefaults["feature-image-top"].category,
categoryTypography = componentDefaults["feature-image-top"].categoryTypography,
categoryVisible = true,
categoryId,
title = componentDefaults["feature-image-top"].title,
titleTypography = componentDefaults["feature-image-top"].titleTypography,
subtitle = componentDefaults["feature-image-top"].subtitle,
subtitleTypography = componentDefaults["feature-image-top"].subtitleTypography,
buttonText = componentDefaults["feature-image-top"].buttonText,
buttonUrl = componentDefaults["feature-image-top"].buttonUrl,
buttonOpenInNewTab = componentDefaults["feature-image-top"].buttonOpenInNewTab || false,
buttonVisible = componentDefaults["feature-image-top"].buttonVisible,
buttonLinkType = componentDefaults["feature-image-top"].buttonLinkType || "url",
buttonTargetDialogId = componentDefaults["feature-image-top"].buttonTargetDialogId,
buttonIconLeft = componentDefaults["feature-image-top"].buttonIconLeft,
buttonIconRight = componentDefaults["feature-image-top"].buttonIconRight,
secondaryButtonText = componentDefaults["feature-image-top"].secondaryButtonText,
secondaryButtonUrl = componentDefaults["feature-image-top"].secondaryButtonUrl,
secondaryButtonOpenInNewTab = componentDefaults["feature-image-top"].secondaryButtonOpenInNewTab || false,
secondaryButtonVisible = componentDefaults["feature-image-top"].secondaryButtonVisible,
secondaryButtonLinkType = componentDefaults["feature-image-top"].secondaryButtonLinkType || "url",
secondaryButtonTargetDialogId = componentDefaults["feature-image-top"].secondaryButtonTargetDialogId,
secondaryButtonIconLeft = componentDefaults["feature-image-top"].secondaryButtonIconLeft,
secondaryButtonIconRight = componentDefaults["feature-image-top"].secondaryButtonIconRight,
image = componentDefaults["feature-image-top"].image,
imageId,
imageVisible,
imageUrl,
imageOpenInNewTab = componentDefaults["feature-image-top"].imageOpenInNewTab || false,
imageLinkType,
imageTargetDialogId,
buttonStyle = "primary",
secondaryButtonStyle = "ghost",
buttonId,
secondaryButtonId,
onUpdate,
sectionId,
fullWidth,
removePaddingLeft,
removePaddingRight,
titleVisible = true,
subtitleVisible = true,
titleId,
subtitleId,
imageRatio,
imageEnableAudio,
imageAutoplay = componentDefaults["feature-image-top"].imageAutoplay,
imageShowStroke,
imageShowShadow,
align = componentDefaults["feature-image-top"]?.align || "left"
}) {
const update = createUpdateHandler(onUpdate);
const alignClass = align === "right" ? "offset-desktop-6 offset-tablet-4" : "";
return (
<BuilderSection
tagName="section"
className={`${styles.section}`}
sectionId={sectionId}
fullWidth={fullWidth}
showFullWidthControl={false}
removePaddingLeft={removePaddingLeft || fullWidth}
removePaddingRight={removePaddingRight || fullWidth}
innerContainer={true}
showTypographyToggle={true}
showAlignToggle={true}
align={align}
titleTypography={titleTypography}
subtitleTypography={subtitleTypography}
categoryTypography={categoryTypography}
>
<div className="grid">
<div className={`col-mobile-4 col-tablet-4 col-desktop-6 ${alignClass} ${styles.columnWrapper}`}>
<div className={`aspectRatioWrapper ${styles.imageWrapper}`}>
<BuilderImage
src={image}
aspectRatio={imageRatio || componentDefaults["feature-image-top"]?.imageRatio || "1-1"}
style={{ aspectRatio: (imageRatio || componentDefaults["feature-image-top"]?.imageRatio || '1-1').replace('-', '/') }}
className="w-full"
id={imageId}
sectionId={sectionId}
isVisible={imageVisible}
suffix="image"
href={imageUrl}
openInNewTab={imageOpenInNewTab}
onOpenInNewTabChange={update('imageOpenInNewTab')}
linkType={imageLinkType}
targetDialogId={imageTargetDialogId}
enableAudio={imageEnableAudio}
autoplay={imageAutoplay}
showStroke={imageShowStroke}
showShadow={imageShowShadow}
/>
</div>
<div className={styles.content}>
{categoryVisible && (
<BuilderText
tagName="div"
className={categoryTypography}
style={{ color: "var(--content-brand--brand)", marginBottom: "var(--gap-sm)" }}
content={category}
onChange={update('category')}
sectionId={sectionId}
id={categoryId}
suffix="category"
/>
)}
{titleVisible && (
<BuilderText
tagName="h2"
className={`${titleTypography} ${styles.title}`}
style={{ marginBottom: "var(--gap-sm)" }}
content={title}
onChange={update('title')}
sectionId={sectionId}
id={titleId}
suffix="title"
/>
)}
{subtitleVisible && (
<BuilderText
tagName="div"
className={subtitleTypography}
style={{ color: "var(--content-neutral--body)", marginBottom: "var(--gap-md)" }}
content={subtitle}
onChange={update('subtitle')}
sectionId={sectionId}
id={subtitleId}
suffix="subtitle"
/>
)}
<div className="buttonWrapperLeft" suppressHydrationWarning>
{buttonVisible && (
<BuilderButton
label={buttonText}
href={buttonUrl}
openInNewTab={buttonOpenInNewTab}
onOpenInNewTabChange={update('buttonOpenInNewTab')}
isVisible={buttonVisible}
sectionId={sectionId}
className={`btn btn-${buttonStyle} btn-lg`}
iconLeft={buttonIconLeft}
iconRight={buttonIconRight}
onVariantChange={update('buttonStyle')}
linkType={buttonLinkType}
targetDialogId={buttonTargetDialogId}
onIconLeftChange={update('buttonIconLeft')}
onIconRightChange={update('buttonIconRight')}
id={buttonId}
suffix="button"
/>
)}
{secondaryButtonVisible && (
<BuilderButton
label={secondaryButtonText}
href={secondaryButtonUrl}
openInNewTab={secondaryButtonOpenInNewTab}
onOpenInNewTabChange={update('secondaryButtonOpenInNewTab')}
isVisible={secondaryButtonVisible}
sectionId={sectionId}
className={`btn btn-${secondaryButtonStyle} btn-lg`}
onVariantChange={update('secondaryButtonStyle')}
linkType={secondaryButtonLinkType}
targetDialogId={secondaryButtonTargetDialogId}
iconLeft={secondaryButtonIconLeft}
iconRight={secondaryButtonIconRight}
onIconLeftChange={update('secondaryButtonIconLeft')}
onIconRightChange={update('secondaryButtonIconRight')}
id={secondaryButtonId}
suffix="secondary-button"
/>
)}
</div>
</div>
</div>
</div>
</BuilderSection>
);
}